import React from 'react'
import Button from '../buttons/Button'

interface TrainingCardProps {
  status: 'Completed' | 'Ongoing' | 'Upcoming' | 'Current'
  mode: string
  title: string
  date: string
  buttonText: string
  buttonDisabled?: boolean
  imageUrl: string
  onClick?: () => void
}

const CustomCard: React.FC<TrainingCardProps> = ({
  status,
  mode,
  title,
  date,
  buttonText,
  buttonDisabled = false,
  imageUrl,
  onClick,
}) => {
  const statusColorMap: Record<string, string> = {
    Completed: 'bg-green-100 text-green-800 border border-green-200',
    Ongoing: 'bg-orange-100 text-orange-800 border border-orange-200',
    Current: 'bg-primaryBlue border-blue-200 text-white',
    Upcoming: 'bg-red-600 text-white border-red-700',
  }

  const statusColor =
    statusColorMap[status] || 'bg-gray-100 text-gray-800 border border-gray-200'

  return (
    <div className="w-80 flex-shrink-0 rounded-lg overflow-hidden flex flex-col bg-white border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300">
      {/* Image Container with Badge */}
      <div className="relative">
        <img src={imageUrl} alt={title} className="w-full h-48 object-cover" />
        {/* Status Badge - Top Right */}
        <div className="absolute top-3 right-3">
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${statusColor} shadow-sm`}
          >
            {status}
          </span>
        </div>
      </div>

      {/* Card Content */}
      <div className="p-4 flex-1 flex flex-col min-h-48">
        {/* Mode and Additional Info */}
        <div className="flex justify-between items-center mb-3">
          <span className="text-gray-600 text-sm font-medium bg-gray-100 px-3 py-1 rounded-full">
            {mode}
          </span>
        </div>

        {/* Title */}
        <div className="mb-3 flex-1">
          <h2 className="text-gray-900 font-semibold text-lg leading-tight line-clamp-2 min-h-12">
            {title}
          </h2>
        </div>

        {/* Date */}
        <div className="flex items-center text-gray-500 text-sm mb-4">
          <svg
            className="w-4 h-4 mr-2 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <span className="truncate">{date}</span>
        </div>

        {/* Button */}
        <div className="mt-auto">
          <Button
            label={buttonText}
            size="sm"
            outlined={!buttonDisabled}
            disabled={buttonDisabled}
            className={`w-full ${
              buttonDisabled
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
                : 'bg-white text-green-600 border-green-600 hover:bg-green-600 hover:text-white transition-colors duration-200 font-medium'
            }`}
            onClick={() => {
              if (!buttonDisabled && onClick) {
                onClick()
              }
            }}
          />
        </div>
      </div>
    </div>
  )
}

export default CustomCard
